// src/pages/admin/AdminTournamentDashboard.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useNotification } from '../../contexts/NotificationContext';
import tournamentService from '../../services/tournamentService';

import { HeadingMedium } from 'baseui/typography';
import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import { Spinner } from 'baseui/spinner';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Tag, KIND } from 'baseui/tag';
import { Pagination } from 'baseui/pagination';

const statusToKindMap = {
  'status.ready': KIND.primary,
  'status.countdown': KIND.accent,
  'status.in_progress': KIND.positive,
  'status.paused': KIND.warning,
  'status.finished': KIND.neutral,
  'status.unknown': KIND.black,
};

function determineStatusKey(timer, structure) {
  switch (timer.state) {
    case 'ready':
      return 'status.ready';
    case 'play':
      const currentLevel = structure?.levels?.[timer.rowIndex];
      if (timer.rowIndex === 0 && timer.hasTournamentStarted === false && currentLevel?.type === 'break') {
        return 'status.countdown';
      }
      return 'status.in_progress';
    case 'pause':
      return 'status.paused';
    case 'stop':
      return 'status.finished';
    default:
      return 'status.unknown';
  }
}

const AdminTournamentDashboard = () => {
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTournaments, setTotalTournaments] = useState(0);
  const { currentUser } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const fetchTournaments = useCallback(async (currentPage, currentRowsPerPage) => {
    setIsLoading(true);
    const { data, error } = await tournamentService.getAllTournaments(currentRowsPerPage, (currentPage - 1) * currentRowsPerPage);
    if (error) {
      showNotification(error.message || t('errors.admin.fetchTournaments'), 'error');
    } else if (data?.tournaments) {
      setTournaments(data.tournaments.map(t => ({
        ...t,
        name: t.name,
        ownerId: t.owner_id,
        createdAt: t.created_at,
        updatedAt: t.updated_at,
        statusKey: determineStatusKey(t.timer_state, t.structure),
      })));
      setTotalTournaments(data.totalCount);
    }
    setIsLoading(false);
  }, [showNotification, t]);

  const handleDeleteTournament = async (tournament) => {
    const tournamentName = tournament.name || tournament.id;
    if (window.confirm(t('admin.tournaments.confirmDelete', { name: tournamentName }))) {
      const { data, error: deleteError } = await tournamentService.adminDeleteTournament(tournament.id);

      if (data?.success) {
        showNotification(t('admin.tournaments.notification.deleteSuccess'), 'success');
        fetchTournaments();
      } else {
        showNotification(deleteError?.message || t('admin.tournaments.notification.deleteError'), 'error');
      }
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchTournaments(page, rowsPerPage);
    } else {
      showNotification(t('errors.admin.notAuthorized'), 'error');
      setIsLoading(false);
    }
  }, [currentUser, fetchTournaments, page, rowsPerPage, showNotification, t]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Block>
      <HeadingMedium>{t('admin.tournaments.title')}</HeadingMedium>
      <TableBuilder data={tournaments}>
        <TableBuilderColumn header={t('admin.tournaments.header.tournamentId')}>
          {(row) => row.id}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.tournaments.header.name')}>
          {(row) => row.name || t('tournament.untitledTournament', { id: row.id.substring(0, 6) })}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.tournaments.header.ownerId')}>
          {(row) => row.ownerId}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.tournaments.header.status')}>
          {(row) => (
            <Tag closeable={false} kind={statusToKindMap[row.statusKey] || KIND.neutral}>
              {t(row.statusKey)}
            </Tag>
          )}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.tournaments.header.lastUpdated')}>
          {(row) => new Date(row.updatedAt).toLocaleString()}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.tournaments.header.actions')}>
          {(row) => (
            <>
              <Button size="compact" onClick={() => navigate(`/controller/${row.id}`)}>
                {t('common.openButton')}
              </Button>
              <Button
                size="compact"
                kind="minimal"
                onClick={() => handleDeleteTournament(row)}
                overrides={{
                  BaseButton: {
                    style: ({ $theme }) => ({
                      color: $theme.colors.negative,
                      marginLeft: '8px',
                    }),
                  },
                }}
              >
                {t('admin.tournaments.actions.delete')}
              </Button>
            </>
          )}
        </TableBuilderColumn>
      </TableBuilder>

      <Pagination
        currentPage={page}
        numPages={Math.ceil(totalTournaments / rowsPerPage)}
        onPageChange={({ nextPage }) => setPage(nextPage)}
        onPrevClick={() => setPage(page - 1)}
        onNextClick={() => setPage(page + 1)}
        onPageSizeChange={({ size }) => setRowsPerPage(size)}
        pageSize={rowsPerPage}
        labels={{
          prevButton: t('common.prev'),
          nextButton: t('common.next'),
        }}
      />
    </Block>
  );
};

export default AdminTournamentDashboard;